import { useEffect, useMemo, useRef, useState } from "react";
import type { CaseRecord, RecordFact } from "../types";
import { parseExtractionConfidence } from "../types";
import { api } from "../api";
import { factMeta, formatFactDate, parseAmount } from "../lib/factMeta";
import { dedupeFacts, amountDedupKey } from "../lib/dedupeFacts";
import {
  buildPartyIndex,
  makePartyUi,
  type PartyCard,
  type PartyUi,
} from "../lib/parties";
import { buildCommitmentRows } from "../lib/commitments";
import {
  buildNeedsAttention,
  OVERVIEW_SECTION_ORDER,
} from "../lib/needsAttention";
import {
  discrepancyRecordIds,
  categoryFactKey,
  type CategoryOverrides,
  type LedgerDisposition,
} from "../lib/financialTotals";
import { buildLedger, type StatusOverride } from "../lib/ledger";
import { scanForExtractionGaps } from "../lib/completeness";
import { formatCents } from "../lib/costsMath";
import {
  summarizeCount,
  summarizeCommitments,
  summarizeFinancial,
} from "../lib/sectionSummary";
import { describeDropFlags } from "../lib/extractionReport";
import {
  loadChronoExpanded,
  saveChronoExpanded,
  loadSectionExpanded,
  saveSectionExpanded,
} from "../lib/viewModeStore";
import FactCard from "./ui/FactCard";
import RecordFactGroup from "./ui/RecordFactGroup";
import FinancialSummary from "./ui/FinancialSummary";
import CommitmentsTable from "./ui/CommitmentsTable";
import FactCategorySection from "./ui/FactCategorySection";
import OverviewSection from "./ui/OverviewSection";
import QuoteContextPanel from "./ui/QuoteContextPanel";
import PartyChips from "./ui/PartyChips";
import PartyPanel from "./ui/PartyPanel";
import ExpenseStatement from "./ExpenseStatement";
import { PROVENANCE_LEGEND } from "../lib/provenance";
import { useEdgeFade } from "../lib/useEdgeFade";

// Status-relevant fact types (Part D #3) — excludes background "statement".
const STATUS_TYPES = new Set([
  "commitment", "decision", "action", "outcome", "amount", "request",
]);

// Chronology date-groups default COLLAPSED once the case outgrows a quick
// read (Round 9, 1B). Explicit per-group toggles persist in localStorage.
const CHRONO_COLLAPSE_RECORDS = 15;
const CHRONO_COLLAPSE_FACTS = 100;

// Local calendar date as YYYY-MM-DD — chronology groups after this render
// with the "upcoming" treatment (1D). Deterministic comparison; fact dates
// are date-only strings.
function localTodayKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

// Deterministic, source-verified replacement for the old AI case summary.
// Assembles verified facts (record_facts) + record metadata into structured
// sections. No AI on render, no Regenerate, no validation badge — every
// fact shown is already substring-verified against its source.
//
// Round 10 order: action above reference — needs-attention, status,
// financial, commitments first; chronology and policy reference below.
// Parties are a chip row under the case header, not a content section.
export default function StructuredOverview({
  caseId,
  caseName = "Case",
  records,
  onNavigateToRecord,
  refreshNonce = 0,
  onShowNeedsReview,
  qaFlagCount = 0,
  onShowQaFlags,
}: {
  caseId: string;
  caseName?: string;
  records: CaseRecord[];
  onNavigateToRecord?: (recordId: string) => void;
  refreshNonce?: number;
  // Jump to the records tab filtered to date-review flags (needs-attention).
  onShowNeedsReview?: () => void;
  // Unaddressed QA/export-gate findings (computed by CaseView from the
  // case's stored validation results) + where to send the user.
  qaFlagCount?: number;
  onShowQaFlags?: () => void;
}) {
  const [facts, setFacts] = useState<RecordFact[]>([]);
  const [loading, setLoading] = useState(true);
  // Extraction progress: per-record counter + a last-activity stamp so a stall
  // (no advance for a stretch) is distinguishable from slow-but-working.
  const [progress, setProgress] = useState<{
    completed: number;
    total: number;
    lastActivityAt: number;
  } | null>(null);
  // Ticks once a second while extracting so the "active Ns ago" heartbeat
  // re-renders without a new progress event arriving.
  const [nowTick, setNowTick] = useState(0);
  // Explicit terminal state — completion is signalled, not inferred from the
  // button re-enabling. Cleared when a new run starts.
  const [extractOutcome, setExtractOutcome] = useState<
    | { stored: number; failed: number; partial?: boolean; failedAtStart?: number | null }
    | { error: string }
    | null
  >(null);
  // Post-extraction zero-drop notice (records whose fact count collapsed).
  const [dropNotice, setDropNotice] = useState<string | null>(null);
  // Participation-evidence windows for the parties gate (server-side scan
  // covers attachment text the list payload doesn't carry).
  const [evidence, setEvidence] = useState<{ windows: Array<{ record_id: string; text: string }> } | undefined>(undefined);
  // Quote-in-context: the fact whose verbatim quote is open in the panel.
  const [quoteView, setQuoteView] = useState<RecordFact | null>(null);
  // Party panel (Round 10): the gated card currently open.
  const [partyView, setPartyView] = useState<PartyCard | null>(null);
  // Expense statement curation overlay (Round 8).
  const [statementOpen, setStatementOpen] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .getParticipationEvidence(caseId)
      .then((r) => live && setEvidence(r))
      .catch(() => {
        /* gate falls back to local record-body tails */
      });
    return () => {
      live = false;
    };
  }, [caseId]);
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .getCaseFacts(caseId)
      .then((r) => live && setFacts(r.facts))
      .catch(() => live && setFacts([]))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [caseId, refreshNonce]);

  // Commitment resolutions: ONE source of truth shared by the main table,
  // the party panel, and the needs-attention block — resolving anywhere
  // updates everywhere (Round 10).
  const [resolvedKeys, setResolvedKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    let live = true;
    api
      .getCommitmentResolutions(caseId)
      .then((r) => live && setResolvedKeys(new Set(r.keys)))
      .catch(() => {
        /* default: nothing resolved */
      });
    return () => {
      live = false;
    };
  }, [caseId]);
  async function toggleResolved(key: string, resolved: boolean) {
    // Optimistic; revert on failure.
    setResolvedKeys((prev) => {
      const next = new Set(prev);
      resolved ? next.add(key) : next.delete(key);
      return next;
    });
    try {
      await api.setCommitmentResolution(caseId, key, resolved);
    } catch {
      setResolvedKeys((prev) => {
        const next = new Set(prev);
        resolved ? next.delete(key) : next.add(key);
        return next;
      });
    }
  }

  // Amount-category overrides: ONE source of truth (Round 13) shared by the
  // Financial summary body AND its collapsed summary row, mirroring the
  // commitment-resolution pattern. Lifted here so the collapsed "N to confirm"
  // count and the in-section selectors never diverge.
  const [overrides, setOverrides] = useState<Map<string, LedgerDisposition>>(
    new Map(),
  );
  // Status + amount + merge inputs to the SHARED ledger (Round 14). The
  // Overview net is now the same buildLedger computation as the Summary tab,
  // so it needs the same inputs (status/amount come from the same
  // amount-categories response; merge decisions from merge-links).
  const [statusOverrides, setStatusOverrides] = useState<
    Map<string, StatusOverride>
  >(new Map());
  const [mergeLinks, setMergeLinks] = useState<
    Map<string, "merge" | "separate">
  >(new Map());
  useEffect(() => {
    let live = true;
    api
      .getAmountCategories(caseId)
      .then((r) => {
        if (!live) return;
        setOverrides(
          new Map(
            r.overrides.map((o) => [o.fact_key, o.bucket as LedgerDisposition]),
          ),
        );
        setStatusOverrides(
          new Map(
            r.overrides
              .filter((o) => o.status != null || o.amount_cents_override != null)
              .map((o) => [
                o.fact_key,
                {
                  status: (o.status ?? null) as StatusOverride["status"],
                  amountCentsOverride: o.amount_cents_override ?? null,
                },
              ]),
          ),
        );
      })
      .catch(() => {
        /* default: no overrides */
      });
    api
      .getMergeLinks(caseId)
      .then((r) => {
        if (live) setMergeLinks(new Map(r.links.map((l) => [l.pair_key, l.decision as "merge" | "separate"])));
      })
      .catch(() => {
        /* default: no merge links */
      });
    return () => {
      live = false;
    };
  }, [caseId]);
  // bucket === null clears the override (undo → back to AI default /
  // unconfirmed), so a fold guess or manual exclusion is always reversible.
  async function setBucket(fact: RecordFact, bucket: LedgerDisposition | null) {
    const key = categoryFactKey(fact);
    const prev = overrides.get(key);
    setOverrides((m) => {
      const next = new Map(m);
      if (bucket === null) next.delete(key);
      else next.set(key, bucket);
      return next;
    });
    try {
      await api.setAmountCategory(caseId, key, bucket);
    } catch {
      setOverrides((m) => {
        const next = new Map(m);
        if (prev) next.set(key, prev);
        else next.delete(key);
        return next;
      });
    }
  }

  const recordById = useMemo(
    () => new Map(records.map((r) => [r.id, r])),
    [records],
  );

  async function runExtractAll() {
    setProgress({ completed: 0, total: records.length, lastActivityAt: Date.now() });
    setDropNotice(null);
    setExtractOutcome(null);
    try {
      const res = await api.extractAllFacts(caseId, (p) =>
        setProgress({
          completed: p.completed,
          total: p.total,
          lastActivityAt: Date.now(),
        }),
      );
      setFacts(res.facts);
      setDropNotice(describeDropFlags(res.flagged));
      setExtractOutcome({
        stored: res.stored,
        failed: res.failed.length,
        partial: res.partial,
        failedAtStart: res.failedAtStart,
      });
    } catch (e) {
      console.error(e);
      setExtractOutcome({
        error: e instanceof Error ? e.message : "extraction failed",
      });
    } finally {
      setProgress(null);
    }
  }

  // Heartbeat ticker — runs only while a progress object is present.
  useEffect(() => {
    if (!progress) return;
    const id = window.setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [progress !== null]);

  // Party index (Round 10): gated cards PLUS raw-value canonicalization,
  // shared by chips, fact cards, commitment rows, and rollup party lines.
  const partyIndex = useMemo(
    () => buildPartyIndex(facts, records, evidence),
    [facts, records, evidence],
  );
  const parties = partyIndex.cards;
  const partyUi: PartyUi = useMemo(
    () => makePartyUi(partyIndex, setPartyView),
    [partyIndex],
  );
  const partyFactCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of facts) {
      const card = f.party ? partyIndex.cardOf(f.party) : null;
      if (card) m.set(card.name, (m.get(card.name) ?? 0) + 1);
    }
    return m;
  }, [facts, partyIndex]);

  // Split facts by category (migration 0043). The primary views show only
  // 'case_event' facts; coverage terms, valuations, and background history
  // each get their own section so they never clutter the dispute timeline.
  const byCategory = useMemo(() => {
    const cat = (f: RecordFact) => f.fact_category ?? "case_event";
    return {
      caseEvent: facts.filter((f) => cat(f) === "case_event"),
      coverage: facts.filter((f) => cat(f) === "coverage_term"),
      valuation: facts.filter((f) => cat(f) === "valuation"),
      background: facts.filter((f) => cat(f) === "background"),
    };
  }, [facts]);
  const caseEventFacts = byCategory.caseEvent;

  const types = useMemo(
    () => [...new Set(caseEventFacts.map((f) => f.fact_type))],
    [caseEventFacts],
  );
  const shown = useMemo(
    () =>
      caseEventFacts.filter(
        (f) => typeFilter.size === 0 || typeFilter.has(f.fact_type),
      ),
    [caseEventFacts, typeFilter],
  );

  // Dedupe forwarded-thread duplicates by verbatim quote. Each survivor is
  // sourced from its earliest record and carries the others as `alsoIn`.
  const shownDeduped = useMemo(
    () => dedupeFacts(shown, recordById),
    [shown, recordById],
  );
  const alsoInByFactId = useMemo(
    () => new Map(shownDeduped.map((d) => [d.fact.id, d.alsoIn])),
    [shownDeduped],
  );

  // Chronological grouping: date → per-record groups (Part D #4).
  const groups = useMemo(() => {
    const byDate = new Map<string, Map<string, RecordFact[]>>();
    for (const { fact: f } of shownDeduped) {
      const d = f.date ?? "";
      let recs = byDate.get(d);
      if (!recs) byDate.set(d, (recs = new Map()));
      (recs.get(f.record_id) ?? recs.set(f.record_id, []).get(f.record_id)!).push(f);
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => (!a ? 1 : !b ? -1 : a < b ? -1 : 1))
      .map(([date, recs]) => ({ date, records: [...recs.entries()] }));
  }, [shownDeduped]);

  // Current status: most recent MEANINGFUL facts (exclude "statement"),
  // deduplicated so a forwarded thread doesn't crowd out the list.
  const recent = useMemo(
    () =>
      dedupeFacts(
        caseEventFacts.filter((f) => f.date && STATUS_TYPES.has(f.fact_type)),
        recordById,
      )
        .sort((a, b) => (a.fact.date! < b.fact.date! ? 1 : -1))
        .slice(0, 5),
    [caseEventFacts, recordById],
  );

  const toggleType = (t: string) =>
    setTypeFilter((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });

  // ---- Chronology collapse state (1B): explicit toggles persisted per
  // case; everything else follows the size-based default. ----
  const chronoCollapsedDefault =
    records.length > CHRONO_COLLAPSE_RECORDS || facts.length > CHRONO_COLLAPSE_FACTS;
  const [chronoOpen, setChronoOpen] = useState<Record<string, boolean>>(() =>
    loadChronoExpanded(caseId),
  );
  useEffect(() => {
    setChronoOpen(loadChronoExpanded(caseId));
  }, [caseId]);
  const isChronoOpen = (key: string) => chronoOpen[key] ?? !chronoCollapsedDefault;
  const toggleChrono = (key: string) =>
    setChronoOpen((prev) => {
      const next = { ...prev, [key]: !(prev[key] ?? !chronoCollapsedDefault) };
      saveChronoExpanded(caseId, next);
      return next;
    });
  const today = localTodayKey();

  // ---- Commitments + needs-attention (Round 10, 3A): pure re-surfacing
  // of existing deterministic states. ----
  const commitmentRows = useMemo(
    () => buildCommitmentRows(caseEventFacts, recordById, resolvedKeys, today),
    [caseEventFacts, recordById, resolvedKeys, today],
  );
  const needsReviewCount = useMemo(
    () =>
      records.filter(
        (r) => parseExtractionConfidence(r.extraction_confidence)?.date === "fallback",
      ).length,
    [records],
  );
  const attention = useMemo(
    () =>
      buildNeedsAttention({
        commitmentRows,
        needsReviewCount,
        qaFlagCount,
        todayIso: today,
      }),
    [commitmentRows, needsReviewCount, qaFlagCount, today],
  );

  // ---- Section collapse (Round 13, Phase 1): every section below
  // "Needs attention" defaults COLLAPSED to a counted summary row. Explicit
  // toggles persist per case (mirrors the chronology pattern). ----
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>(() =>
    loadSectionExpanded(caseId),
  );
  useEffect(() => {
    setSectionOpen(loadSectionExpanded(caseId));
  }, [caseId]);
  const isSectionOpen = (id: string) => sectionOpen[id] ?? false;
  const anyOpen = Object.values(sectionOpen).some((v) => v === true);
  const toggleSection = (id: string) => {
    const willOpen = !(sectionOpen[id] ?? false);
    setSectionOpen((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? false) };
      saveSectionExpanded(caseId, next);
      return next;
    });
    // Collapsing a section the user scrolled into: bring its header back to
    // the top so they aren't stranded at the bottom of the shrinking page.
    if (!willOpen)
      requestAnimationFrame(() =>
        document.getElementById(id)?.scrollIntoView({ block: "start" }),
      );
  };
  // One-tap return to the scannable all-collapsed Overview, regardless of how
  // many sections are open.
  const collapseAll = () => {
    setSectionOpen({});
    saveSectionExpanded(caseId, {});
    requestAnimationFrame(() =>
      document.getElementById("ov-attention")?.scrollIntoView({ block: "start" }),
    );
  };

  // ---- Deterministic section summaries (Round 13): actionable vs. ambient.
  // Financial reuses the lifted overrides so "N to confirm" matches the body. ----
  // THE shared ledger — same computation as the Summary tab + FinancialSummary
  // body, so the Overview net agrees by construction.
  const ledger = useMemo(
    () =>
      buildLedger(facts, {
        bucketOverrides: overrides as CategoryOverrides,
        statusOverrides,
        mergeLinks,
      }),
    [facts, overrides, statusOverrides, mergeLinks],
  );
  const financialGaps = useMemo(
    () => scanForExtractionGaps([...recordById.values()], facts),
    [recordById, facts],
  );
  const hasFinancial =
    ledger.items.length > 0 ||
    ledger.excluded.length > 0 ||
    financialGaps.length > 0;
  const financialSummary = useMemo(
    () =>
      summarizeFinancial({
        netLabel: ledger.net.asserted ? formatCents(ledger.net.netCents) : null,
        toConfirm: ledger.toConfirm.length,
        discrepancies: discrepancyRecordIds(facts).size,
      }),
    [ledger, facts],
  );
  const commitmentsSummary = useMemo(
    () => summarizeCommitments(commitmentRows, today),
    [commitmentRows, today],
  );
  const statusSummary = summarizeCount(recent.length, "recent update", "recent updates");
  const chronologySummary = summarizeCount(groups.length, "date");

  // ---- Section nav (1A): anchored jump links, sticky, active on scroll.
  // Order comes from OVERVIEW_SECTION_ORDER (Round 10 — action above
  // reference); parties are NOT a section. ----
  const navSections = useMemo(() => {
    const hasAmounts = facts.some(
      (f) => f.fact_type === "amount" && parseAmount(f.amount) !== 0,
    );
    const presence: Record<string, boolean> = {
      "ov-attention": attention.length > 0,
      "ov-status": recent.length > 0,
      "ov-financial": hasAmounts,
      "ov-commitments": commitmentRows.length > 0,
      "ov-chronology": groups.length > 0,
      "ov-coverage": byCategory.coverage.length > 0,
      "ov-valuation": byCategory.valuation.length > 0,
    };
    return OVERVIEW_SECTION_ORDER.filter((s) => presence[s.id]);
  }, [facts, attention, recent, commitmentRows, groups, byCategory]);

  const [activeSection, setActiveSection] = useState("");
  const sectionVisibleRef = useRef<Map<string, boolean>>(new Map());
  const navIdsKey = navSections.map((s) => s.id).join("|");
  // Edge fade for the sticky section nav (it scrolls within itself on
  // narrow screens with hidden scrollbars).
  const navRef = useEdgeFade<HTMLElement>(navIdsKey);
  useEffect(() => {
    const ids = navIdsKey ? navIdsKey.split("|") : [];
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;
    const vis = sectionVisibleRef.current;
    vis.clear();
    // Active section = topmost one inside the upper viewport band.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) vis.set(e.target.id, e.isIntersecting);
        const active = ids.find((id) => vis.get(id));
        if (active) setActiveSection(active);
      },
      { rootMargin: "-10% 0px -60% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [navIdsKey, loading]);

  const jumpTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const attentionTap = (kind: string, targetId?: string) => {
    if (kind === "needs_review") onShowNeedsReview?.();
    else if (kind === "qa_flags") onShowQaFlags?.();
    else if (targetId) jumpTo(targetId);
  };

  return (
    <div className="structured-overview">
      {/* Parties: identity/context, not a content section — one compact
          chip row directly under the case header (Round 10). */}
      <PartyChips
        cards={parties}
        factCounts={partyFactCounts}
        onOpen={setPartyView}
      />

      <div className="overview-head">
        <h2 className="overview-title">Case Overview</h2>
        <button
          type="button"
          className="btn-secondary"
          disabled={!!progress}
          onClick={runExtractAll}
        >
          {progress
            ? `Extracting… ${progress.completed}/${progress.total}`
            : facts.length === 0
              ? "Extract all facts"
              : "Re-extract facts"}
        </button>
      </div>

      {progress && (
        <ExtractionHeartbeat progress={progress} nowTick={nowTick} />
      )}
      {!progress && extractOutcome && (
        <p
          className={
            "error" in extractOutcome || ("partial" in extractOutcome && extractOutcome.partial)
              ? "overview-extract-status failed"
              : extractOutcome.failed > 0
                ? "overview-extract-status warn"
                : "overview-extract-status done"
          }
          role="status"
        >
          {"error" in extractOutcome ? (
            `Extraction stopped: ${extractOutcome.error}`
          ) : extractOutcome.partial ? (
            <>
              {`Extraction incomplete — ${extractOutcome.stored} fact${extractOutcome.stored === 1 ? "" : "s"} stored, then stopped at a problem chunk. `}
              <button type="button" className="rs-link" onClick={runExtractAll}>Retry</button>
            </>
          ) : (
            `Done — ${extractOutcome.stored} fact${extractOutcome.stored === 1 ? "" : "s"} stored${
              extractOutcome.failed > 0
                ? `, ${extractOutcome.failed} record${extractOutcome.failed === 1 ? "" : "s"} failed`
                : ""
            }.`
          )}
        </p>
      )}

      {loading && <p className="overview-muted">Loading facts…</p>}

      {!loading && (navSections.length > 1 || anyOpen) && (
        <div className="overview-toolbar">
          {navSections.length > 1 && (
            <nav className="overview-nav h-scroll-fade" aria-label="Overview sections" ref={navRef}>
              {navSections.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`overview-nav-link${activeSection === s.id ? " active" : ""}`}
                  onClick={() => jumpTo(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </nav>
          )}
          {anyOpen && (
            <button
              type="button"
              className="overview-collapse-all"
              onClick={collapseAll}
            >
              Collapse all ▴
            </button>
          )}
        </div>
      )}

      {dropNotice && (
        <div className="overview-banner">
          {dropNotice}{" "}
          <button type="button" className="fact-source-link" onClick={() => setDropNotice(null)}>
            Dismiss
          </button>
        </div>
      )}

      {!loading && facts.length === 0 && !progress && (
        <div className="overview-banner">
          Facts have not been extracted for this case. Run “Extract all facts”
          (here or via Maintain) to populate detailed event information.
        </div>
      )}

      {facts.length > 0 && (
        <>
          {/* 1. NEEDS ATTENTION — deterministic re-surfacing only. */}
          <section className="overview-section overview-anchor" id="ov-attention">
            <h3 className="overview-section-title">Needs attention</h3>
            {attention.length === 0 ? (
              <p className="overview-muted attention-clear attention-uptodate">
                ✓ You’re up to date — nothing is overdue or awaiting review.
              </p>
            ) : (
              <ul className="attention-list">
                {attention.map((item, i) => (
                  <li key={`${item.kind}-${i}`}>
                    <button
                      type="button"
                      className={`attention-item attention-${item.kind}`}
                      onClick={() => attentionTap(item.kind, item.targetId)}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* 2. CURRENT STATUS — capped at 5, explicit recency line (3B). */}
          {recent.length > 0 && (
            <OverviewSection
              id="ov-status"
              title="Current status"
              summary={statusSummary}
              open={isSectionOpen("ov-status")}
              onToggle={() => toggleSection("ov-status")}
            >
              {recent.map(({ fact: f, alsoIn }) => {
                const rec = recordById.get(f.record_id);
                return (
                  <div key={`recent-${f.id}`} className="status-item">
                    <FactCard
                      fact={f}
                      record={rec}
                      onNavigateToRecord={onNavigateToRecord}
                      onViewQuote={setQuoteView}
                      alsoIn={alsoIn}
                      recordById={recordById}
                      hideSource
                      partyUi={partyUi}
                    />
                    <div className="status-source-line overview-muted">
                      from{" "}
                      <button
                        type="button"
                        className="fact-source-link"
                        onClick={() => onNavigateToRecord?.(f.record_id)}
                      >
                        {rec?.subject?.trim() || "record"}
                      </button>
                      , {formatFactDate(rec?.record_date?.slice(0, 10) ?? null)}
                    </div>
                  </div>
                );
              })}
            </OverviewSection>
          )}

          {/* 3. FINANCIAL SUMMARY. ALL facts: the intra-record fold pool
              must see amounts from every category (display placement stays
              per-section). The expense-statement export lives inside the
              section so nothing renders at full depth by default. */}
          {hasFinancial && (
            <OverviewSection
              id="ov-financial"
              title="Financial summary"
              summary={financialSummary}
              open={isSectionOpen("ov-financial")}
              onToggle={() => toggleSection("ov-financial")}
            >
              <FinancialSummary
                facts={facts}
                recordById={recordById}
                onNavigateToRecord={onNavigateToRecord}
                overrides={overrides}
                statusOverrides={statusOverrides}
                mergeLinks={mergeLinks}
                onSetBucket={(f, b) => void setBucket(f, b)}
              />
              <p>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setStatementOpen(true)}
                >
                  Expense statement — curate &amp; export →
                </button>
              </p>
            </OverviewSection>
          )}

          {/* 4. COMMITMENTS & DEADLINES — shared resolution state. */}
          {commitmentRows.length > 0 && (
            <OverviewSection
              id="ov-commitments"
              title="Commitments &amp; deadlines"
              summary={commitmentsSummary}
              open={isSectionOpen("ov-commitments")}
              onToggle={() => toggleSection("ov-commitments")}
            >
              <CommitmentsTable
                rows={commitmentRows}
                recordById={recordById}
                onToggleResolved={(k, r) => void toggleResolved(k, r)}
                onNavigateToRecord={onNavigateToRecord}
                partyUi={partyUi}
              />
            </OverviewSection>
          )}

          {/* 5. CHRONOLOGY. The type filter lives inside the section body so a
              collapsed chronology stays a single summary row. */}
          <OverviewSection
            id="ov-chronology"
            title="Chronological facts"
            summary={chronologySummary}
            open={isSectionOpen("ov-chronology")}
            onToggle={() => toggleSection("ov-chronology")}
          >
            {types.length > 1 && (
              <div className="fact-type-filter">
                {types.map((t) => {
                  const m = factMeta(t);
                  const on = typeFilter.has(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      className={`fact-type-chip${on ? " on" : ""}`}
                      style={on ? { borderColor: m.color, color: m.color } : undefined}
                      onClick={() => toggleType(t)}
                    >
                      {m.emoji} {m.label}
                    </button>
                  );
                })}
              </div>
            )}
            {groups.map(({ date, records: recGroups }) => {
              const key = date || "undated";
              const open = isChronoOpen(key);
              const upcoming = !!date && date.slice(0, 10) > today;
              const factCount = recGroups.reduce((s, [, items]) => s + items.length, 0);
              // 1C: one-fact "mentioned in" groups (record dated on another
              // day) merge into a single collapsed group under this date.
              const isMention = ([recordId, items]: [string, RecordFact[]]) => {
                if (items.length !== 1 || !date) return false;
                const rd = recordById.get(recordId)?.record_date?.slice(0, 10);
                return !!rd && rd !== date.slice(0, 10);
              };
              const mentions = recGroups.filter(isMention);
              const merged = mentions.length >= 2;
              const normal = merged
                ? recGroups.filter((g) => !mentions.includes(g))
                : recGroups;
              return (
                <div
                  key={key}
                  className={`fact-date-group${upcoming ? " fact-date-group-upcoming" : ""}`}
                >
                  <button
                    type="button"
                    className="fact-date-toggle"
                    onClick={() => toggleChrono(key)}
                    aria-expanded={open}
                  >
                    <span className="fact-rec-caret">{open ? "▾" : "▸"}</span>
                    <span className="fact-date-header">{formatFactDate(date || null)}</span>
                    {upcoming && (
                      <span className="fact-date-upcoming-flag">
                        upcoming — scheduled/estimated date
                      </span>
                    )}
                    <span className="fact-rec-count">
                      {factCount} fact{factCount === 1 ? "" : "s"} ·{" "}
                      {recGroups.length} record{recGroups.length === 1 ? "" : "s"}
                    </span>
                  </button>
                  {open && (
                    <>
                      {normal.map(([recordId, items]) => (
                        <RecordFactGroup
                          key={recordId}
                          record={recordById.get(recordId)}
                          facts={items}
                          onNavigateToRecord={onNavigateToRecord}
                          onViewQuote={setQuoteView}
                          alsoInByFactId={alsoInByFactId}
                          recordById={recordById}
                          groupDate={date || null}
                          partyUi={partyUi}
                        />
                      ))}
                      {merged && (
                        <MergedMentions
                          groups={mentions}
                          recordById={recordById}
                          onNavigateToRecord={onNavigateToRecord}
                          onViewQuote={setQuoteView}
                          alsoInByFactId={alsoInByFactId}
                          partyUi={partyUi}
                        />
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </OverviewSection>

          {/* 6–7. REFERENCE: coverage terms, valuations. */}
          <FactCategorySection
            id="ov-coverage"
            title="Coverage &amp; policy terms"
            facts={byCategory.coverage}
            recordById={recordById}
            onNavigateToRecord={onNavigateToRecord}
            onViewQuote={setQuoteView}
            open={isSectionOpen("ov-coverage")}
            onToggle={() => toggleSection("ov-coverage")}
            countNoun="term"
            keyOf={amountDedupKey}
            partyUi={partyUi}
          />
          <FactCategorySection
            id="ov-valuation"
            title="Valuation &amp; assessments"
            facts={byCategory.valuation}
            recordById={recordById}
            onNavigateToRecord={onNavigateToRecord}
            onViewQuote={setQuoteView}
            open={isSectionOpen("ov-valuation")}
            onToggle={() => toggleSection("ov-valuation")}
            countNoun="entry"
            keyOf={amountDedupKey}
            partyUi={partyUi}
          />

          <FactCategorySection
            id="ov-background"
            title="Background context"
            facts={byCategory.background}
            recordById={recordById}
            onNavigateToRecord={onNavigateToRecord}
            onViewQuote={setQuoteView}
            open={isSectionOpen("ov-background")}
            onToggle={() => toggleSection("ov-background")}
            partyUi={partyUi}
          />

          <p className="overview-muted provenance-legend" title={PROVENANCE_LEGEND}>
            <span className="confidence-dot confidence-green prov-mark" /> verified
            against your documents · <span className="prov-ai-chip">AI</span> AI
            assistance — {PROVENANCE_LEGEND}
          </p>
        </>
      )}

      {partyView && (
        <PartyPanel
          party={partyView}
          caseId={caseId}
          facts={facts}
          records={records}
          recordById={recordById}
          evidence={evidence}
          isMine={(raw) =>
            !!raw && partyIndex.cardOf(raw)?.name === partyView.name
          }
          commitmentRows={commitmentRows}
          onToggleResolved={(k, r) => void toggleResolved(k, r)}
          onNavigateToRecord={onNavigateToRecord}
          onViewQuote={setQuoteView}
          partyUi={partyUi}
          onClose={() => setPartyView(null)}
        />
      )}

      {quoteView && (
        <QuoteContextPanel
          fact={quoteView}
          record={recordById.get(quoteView.record_id)}
          onClose={() => setQuoteView(null)}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}

      {statementOpen && (
        <ExpenseStatement
          caseId={caseId}
          caseName={caseName}
          records={records}
          onClose={() => setStatementOpen(false)}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}
    </div>
  );
}

// Round 9 (1C): multiple one-fact "mentioned in <record>" groups under the
// SAME chronology date merge into one collapsed group. Each fact keeps its
// own source label ("mentioned in <record> (<date>)") via FactCard.
function MergedMentions({
  groups,
  recordById,
  onNavigateToRecord,
  onViewQuote,
  alsoInByFactId,
  partyUi,
}: {
  groups: Array<[string, RecordFact[]]>;
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
  onViewQuote?: (fact: RecordFact) => void;
  alsoInByFactId?: Map<string, string[]>;
  partyUi?: PartyUi;
}) {
  const [open, setOpen] = useState(false);
  const facts = groups.flatMap(([, items]) => items);
  return (
    <div className="fact-rec-group">
      <button
        type="button"
        className="fact-rec-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="fact-rec-caret">{open ? "▾" : "▸"}</span>
        <span className="fact-rec-title">
          Mentioned in {groups.length} other records
        </span>
        <span className="fact-rec-count">
          {facts.length} fact{facts.length === 1 ? "" : "s"}
        </span>
      </button>
      {open && (
        <div className="fact-rec-body">
          {facts.map((f) => (
            <FactCard
              key={f.id}
              fact={f}
              record={recordById.get(f.record_id)}
              onNavigateToRecord={onNavigateToRecord}
              onViewQuote={onViewQuote}
              alsoIn={alsoInByFactId?.get(f.id) ?? []}
              recordById={recordById}
              partyUi={partyUi}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Per-record progress + liveness. Each record advances the counter and stamps
// lastActivityAt; the "active Ns ago" line (re-rendered by nowTick) makes a
// stall visible — past STALL_HINT_SEC it reads as possibly-stuck rather than
// frozen-with-no-explanation.
const STALL_HINT_SEC = 25;
function ExtractionHeartbeat({
  progress,
  nowTick,
}: {
  progress: { completed: number; total: number; lastActivityAt: number };
  // Forces a re-render each second so the elapsed time below stays current.
  nowTick: number;
}) {
  void nowTick;
  const idleSec = Math.max(0, Math.round((Date.now() - progress.lastActivityAt) / 1000));
  const stalled = idleSec >= STALL_HINT_SEC;
  const pct =
    progress.total > 0
      ? Math.min(100, Math.round((progress.completed / progress.total) * 100))
      : 0;
  return (
    <p
      className={`overview-extract-status ${stalled ? "warn" : "running"}`}
      role="status"
      aria-live="polite"
    >
      Extracting {progress.completed}/{progress.total} ({pct}%) ·{" "}
      {stalled
        ? `no progress for ${idleSec}s — still working or stuck`
        : idleSec <= 1
          ? "active just now"
          : `active ${idleSec}s ago`}
    </p>
  );
}
